import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React, { useEffect, useState, useRef, createRef } from "react";
import { RouteProps } from "react-router";
import { v4 as uuidv4 } from "uuid";
import config from "../../../components/helpers/socket/config";
import { send } from "../../../utils/sockets/asyncSocket";
import { CMEDPermissions } from "../MarketData/MarketDataContent";
import OptionsGridWrapper from "./OptionsGridWrapper";
import { chevronDownOutline } from "ionicons/icons";
import { unsubscribeStrikeLadder } from "./unsubscribeStrikeLadder";
import { sortContractsByNameFollowUpWithExpiry } from "./utils";
/**
 * @author
 * E45597 Aryamitra Chaudhuri
 * E47440 Vithal Agrawal
 */

interface OptionsGridSuperWrapperProps extends RouteProps {
  userCMEDEntitlements: CMEDPermissions | undefined;
  isUserSettingsModalOpen: any;
  persistedOptionGrids: any;
  setPersistedOptionGrids: any;
}
const OptionsGridSuperWrapper: React.FC<OptionsGridSuperWrapperProps> = ({
  userCMEDEntitlements,
  isUserSettingsModalOpen,
  persistedOptionGrids,
  setPersistedOptionGrids,
}) => {
  const [greekType, setgreekType] = useState<any>("4");
  const [optionGrids, setoptionGrids] = useState<any>([]);
  const [selectedOption, setselectedOption] = useState<any>();
  const [optionsGridGroup, setOptionsGridGroup] = useState<any>(undefined);
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const selectRef = useRef<HTMLIonSelectElement>(null);
  const [sortedOptionGridList, setSortedOptionGridList] = useState<any>([]);
  const [optionsList, setOptionsList] = useState<any>([]);

  const [deletedOptionGridIndex, setdeletedOptionGridIndex] =
    useState<any>(undefined);
  const [lastValidOption, setLastValidOption] = useState<any>(undefined);

  useEffect(() => {
    setoptionGrids(persistedOptionGrids);
    if (persistedOptionGrids?.length > 0) {
      const index =
        deletedOptionGridIndex !== undefined &&
        deletedOptionGridIndex >= 0 &&
        !(deletedOptionGridIndex > persistedOptionGrids?.length - 1)
          ? deletedOptionGridIndex
          : persistedOptionGrids?.length - 1;

      const currentSelectionExists = persistedOptionGrids.some(
        (item: any) =>
          item.TermStructureCode === selectedOption?.TermStructureCode,
      );
      if (deletedOptionGridIndex !== undefined || selectedOption == null) {
        setselectedOption(persistedOptionGrids[index]);
        setgreekType(persistedOptionGrids[index]?.greekSource);
        setdeletedOptionGridIndex(undefined);
      } else if (currentSelectionExists) {
        const updatedItem = persistedOptionGrids.find(
          (item: any) =>
            item.TermStructureCode === selectedOption?.TermStructureCode,
        );
        setselectedOption((prev: any) => {
          if (
            prev?.TermStructureCode === updatedItem?.TermStructureCode &&
            prev?.greekSource === updatedItem?.greekSource
          ) {
            return prev;
          }
          return updatedItem;
        });
        setgreekType(updatedItem?.greekSource);
      }
    } else if (selectedOption == null || deletedOptionGridIndex !== undefined) {
      setselectedOption(undefined);
      setdeletedOptionGridIndex(undefined);
      setgreekType("4");
    }
  }, [persistedOptionGrids]);

  useEffect(() => {
    // This effect handles adding new contracts from the search modal to the grid.
    // It should only run when `selectedOption` is an array of new items.

    if (optionsList?.length > 0) {
      const array: any = [];

      optionGrids?.forEach((element: any) => {
        array.push(element?.TermStructureCode);
      });
      let selectedOptionNew: any = [];
      optionsList?.forEach((element: any, idx: any, array1: any) => {
        if (
          element &&
          element !== undefined &&
          !array.includes(element.TermStructureCode)
        ) {
          selectedOptionNew.push(element);
        }
      });
      if (selectedOptionNew.length > 0) {
        persistNewOptionGrid(selectedOptionNew);
      } else {
        const optionItem = optionsList[optionsList.length - 1];
        setselectedOption(optionItem);
      }
    }
  }, [optionsList]);

  useEffect(() => {
    if (optionGrids == null || optionGrids.length == 0) {
      setSortedOptionGridList([]);
    }
    const sortedList = sortContractsByNameFollowUpWithExpiry([...optionGrids]);
    setSortedOptionGridList(sortedList);
  }, [optionGrids]);

  function persistNewOptionGrid(items: any) {
    let arr = items.map((item: any) => ({
      optionCategory: item?.OptionCategory,
      TermStructureCode: item?.TermStructureCode,
      TermStructureDescription: item?.TermStructureDescription,
      greekSource: greekType.toString(),
      greeksEnabled: true,
    }));
    send(config.socket.channels.CORE, {
      action: "savePersistedOptionGrids",
      type: "legacyValidation",
      requestID: uuidv4(),
      payload: arr && arr.length > 0 ? arr : [],
    });
  }

  const handleIonLabelClick = () => {
    // Use the ref to programmatically open the select
    if (selectRef?.current) {
      selectRef?.current?.open();
      setTimeout(() => {
        setIsSelectOpen(true);
      }, 0);
    }
  };

  const handleIonChange = (e: any) => {
    if (selectedOption) {
      unsubscribeStrikeLadder(selectedOption);
    }
    setselectedOption(e.detail.value);
    setgreekType(e.detail.value?.greekSource);
    setIsSelectOpen(false);
  };

  const handleIonCancel = () => {
    setIsSelectOpen(false);
  };

  useEffect(() => {
    if (selectedOption != null) {
      setLastValidOption(selectedOption);
    }
  }, [selectedOption?.TermStructureCode]);

  const returnToOptionGrid = () => {
    if (lastValidOption) {
      setselectedOption(lastValidOption);
      setgreekType(lastValidOption.greekSource);
    } else if (optionGrids?.length > 0) {
      setselectedOption(optionGrids[optionGrids?.length - 1]);
      setgreekType(optionGrids[optionGrids?.length - 1]?.greekSource);
    }
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle></IonTitle>
        </IonToolbar>
      </IonHeader>
      {selectedOption != null
        ? optionGrids &&
          optionGrids.length > 0 && (
            <IonHeader className="ion-no-border">
              <div className="optionsgrid_header_section ion-no-padding">
                <IonItem className="option_name" onClick={handleIonLabelClick}>
                  <IonLabel>
                    {optionsGridGroup
                      ? selectedOption?.TermStructureDescription +
                        `${` (` + optionsGridGroup?.tt + `)`}`
                      : selectedOption?.TermStructureDescription}
                  </IonLabel>
                  <IonIcon
                    className={`option_name_toggle_icon ${isSelectOpen ? "open" : ""}`}
                    icon={chevronDownOutline}
                    slot="end"
                  ></IonIcon>
                </IonItem>
                <IonSelect
                  interface="action-sheet"
                  disabled={optionGrids?.length === 0}
                  value={selectedOption}
                  onIonChange={handleIonChange}
                  onIonCancel={handleIonCancel}
                  className="ion-no-padding"
                  ref={selectRef}
                  cancelText="Cancel"
                >
                  {sortedOptionGridList?.map((elem: any, index: any) => (
                    <IonSelectOption value={elem} key={index}>
                      {elem?.TermStructureDescription}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </div>
            </IonHeader>
          )
        : null}

      <OptionsGridWrapper
        userCMEDEntitlements={userCMEDEntitlements}
        userSelectedOption={selectedOption}
        persistedOptionGrids={persistedOptionGrids}
        setPersistedOptionGrids={setPersistedOptionGrids}
        setdeletedOptionGridIndex={setdeletedOptionGridIndex}
        greekType={greekType}
        setgreekType={setgreekType}
        setselectedOption_Super={setselectedOption}
        optionsGridGroup={optionsGridGroup}
        setOptionsGridGroup={setOptionsGridGroup}
        optionGrids={optionGrids}
        setOptionsList={setOptionsList}
        returnToOptionGrid={returnToOptionGrid}
      />
    </IonPage>
  );
};

export default OptionsGridSuperWrapper;
